import { type SxProps, type Theme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';

export const CHIP_STYLE_OVERRIDES: SxProps<Theme> = {
    fontWeight: '600 !important',
    fontSize: '0.75rem !important',
    height: '22px !important',
    cursor: 'pointer !important',
};

export const GRAYED_CHIP_STYLE: SxProps<Theme> = {
    ...CHIP_STYLE_OVERRIDES,
    backgroundColor: '#e0e0e0 !important',
    color: '#999 !important',
};

export const PARAGRAPH_HIGHLIGHT_STYLES = {
    highScore: { borderLeft: '3px solid #d32f2f' },
    mediumScore: { borderLeft: '3px solid #ef5350' },
    lowScore: { borderLeft: '3px solid #ff9800' },
    problematic: { borderLeft: '3px solid #d32f2f' },
    ok: { borderLeft: '3px solid #bdbdbd', opacity: 0.6 },
} as const;

export function getHighlightColor(score: number, reviewStatus?: string): string {
    if (reviewStatus === 'problematic') return 'rgba(211, 47, 47, 0.35)';
    if (reviewStatus === 'ok') return 'rgba(0, 0, 0, 0.08)';
    if (score >= 0.85) return 'rgba(211, 47, 47, 0.28)';
    if (score >= 0.7) return 'rgba(211, 47, 47, 0.18)';
    if (score >= 0.5) return 'rgba(255, 152, 0, 0.18)';
    return '#fff9c4';
}

export function getMatchPanelHeaderStyle(reviewStatus?: string): {
    backgroundColor: string;
    color: string;
    icon: 'warning' | 'error' | 'check';
    label: string;
} {
    if (reviewStatus === 'problematic') {
        return { backgroundColor: '#fce4ec', color: '#c62828', icon: 'error', label: 'Problematic' };
    }
    if (reviewStatus === 'ok') {
        return { backgroundColor: '#e8f5e9', color: '#2e7d32', icon: 'check', label: 'Reviewed — OK' };
    }
    return { backgroundColor: '#fff8e1', color: '#f57f17', icon: 'warning', label: 'Needs Review' };
}

export function getSentenceHighlightColor(score: number, reviewStatus?: string): string {
    if (reviewStatus === 'ok') return '#e8f5e9';
    if (score >= 0.85) return 'rgba(211, 47, 47, 0.25)';
    if (score >= 0.7) return 'rgba(211, 47, 47, 0.15)';
    if (score >= 0.5) return 'rgba(255, 152, 0, 0.15)';
    return 'rgba(255, 235, 59, 0.2)';
}

export function getParagraphHighlightStyle(score: number, reviewStatus?: string) {
    if (reviewStatus === 'problematic') return PARAGRAPH_HIGHLIGHT_STYLES.problematic;
    if (reviewStatus === 'ok') return PARAGRAPH_HIGHLIGHT_STYLES.ok;
    if (score >= 0.85) return PARAGRAPH_HIGHLIGHT_STYLES.highScore;
    if (score >= 0.7) return PARAGRAPH_HIGHLIGHT_STYLES.mediumScore;
    if (score >= 0.5) return PARAGRAPH_HIGHLIGHT_STYLES.lowScore;
    return {};
}

// --- Content-classification cues (non-body sections in the document reader) ---
// Muted palette kept clear of the red/orange flagged spectrum and the blue external spectrum,
// so a non-body section reads as "context, not a match". body / non_substantive get no cue.
export const CLASSIFICATION_CUES: Record<string, { color: string; label: string }> = {
    reference: { color: '#00897b', label: 'Reference' },
    ai_disclosure: { color: '#7b1fa2', label: 'AI Disclosure' },
    title_page: { color: '#546e7a', label: 'Title Page' },
    toc: { color: '#6d4c41', label: 'Contents' },
    boilerplate: { color: '#9e9e9e', label: 'Boilerplate' },
};

export function getClassificationCue(classification?: string): { color: string; label: string } | null {
    if (!classification) return null;
    return CLASSIFICATION_CUES[classification] ?? null;
}

export const FLOATING_PANEL_STYLES: SystemStyleObject<Theme> = {
    position: 'relative',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
};

export const FLAGGED_PARAGRAPH_HOVER_SX = {
    '&:hover': { '--highlight-filter': 'brightness(0.88)' },
} as const;

export const BADGE_STYLES: SystemStyleObject<Theme> = {
    minWidth: 24,
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.06)',
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
};

export function getBorderColor(score: number, reviewStatus?: string): string {
    if (reviewStatus === 'problematic') return '#d32f2f';
    if (reviewStatus === 'ok') return '#bdbdbd';
    if (score >= 0.85) return '#d32f2f';
    if (score >= 0.7) return '#ef5350';
    if (score >= 0.5) return '#ff9800';
    return '#bdbdbd';
}

// --- External source highlight colors (blue/indigo spectrum) ---

export const EXTERNAL_PARAGRAPH_HIGHLIGHT_STYLES = {
    default: { borderLeft: '3px solid #3949ab' },
    problematic: { borderLeft: '3px solid #1a237e' },
    ok: { borderLeft: '3px solid #bdbdbd', opacity: 0.6 },
} as const;

export function getExternalSentenceHighlightColor(reviewStatus?: string): string {
    if (reviewStatus === 'ok') return '#e8f5e9';
    return 'rgba(57, 73, 171, 0.18)';
}

export function getExternalParagraphHighlightStyle(reviewStatus?: string) {
    if (reviewStatus === 'problematic') return EXTERNAL_PARAGRAPH_HIGHLIGHT_STYLES.problematic;
    if (reviewStatus === 'ok') return EXTERNAL_PARAGRAPH_HIGHLIGHT_STYLES.ok;
    return EXTERNAL_PARAGRAPH_HIGHLIGHT_STYLES.default;
}

export function getExternalBorderColor(reviewStatus?: string): string {
    if (reviewStatus === 'problematic') return '#1a237e';
    if (reviewStatus === 'ok') return '#bdbdbd';
    return '#3949ab';
}
